import { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import {
  Plus, Pencil, Trash2, LogOut, Loader2, Home, Upload, ImageIcon,
  Users, MessageSquare, Building2, Star, ImageOff, MapPin, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { LogoMark } from "../components/Logo";
import { ImageCropDialog } from "../components/ImageCropDialog";
import {
  getMe, login, clearToken, getToken, getListings,
  createListing, updateListing, deleteListing, uploadImage, resolveImage,
  getTeam, updateTeamMember, getEnquiries, deleteEnquiry,
} from "../lib/api";
import { TEAM } from "../lib/brand";

const STATIC_TEAM = TEAM.map((m, i) => ({ ...m, photo: "", order: i }));

const CATEGORIES = ["Plot", "Builder Floor", "Farmhouse", "Commercial", "Agriculture"];
const DEALS = ["Sale", "Purchase", "Rent"];

const EMPTY = {
  title: "", category: "Plot", deal_type: "Sale", location: "",
  price: "", area: "", bedrooms: "", description: "", image_url: "", images: [], featured: true,
};

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email.trim(), password);
      toast.success("Welcome back, Admin");
      onSuccess(user);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#faf9f5] px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl bg-white border border-[#e7e3d7] p-8 shadow-[0_30px_80px_-40px_rgba(19,19,17,0.35)]"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col items-center text-center mb-7">
          <LogoMark className="h-12 w-12" />
          <h1 className="mt-4 font-serif text-2xl font-semibold text-[#131311]">Admin Portal</h1>
          <p className="text-sm text-[#8a8578]">Laxmi Jiwan Properties</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email" required autoComplete="off" data-testid="admin-email-input" className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password" required autoComplete="off" data-testid="admin-password-input" className="mt-1.5"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <Button
            type="submit" disabled={loading}
            className="w-full bg-[#131311] hover:bg-[#9a7b38] rounded-full py-6"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ListingForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (initial) {
      const imgs = initial.images && initial.images.length
        ? initial.images
        : (initial.image_url ? [initial.image_url] : []);
      setForm({ ...EMPTY, ...initial, images: imgs });
    } else {
      setForm(EMPTY);
    }
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropSrc(URL.createObjectURL(file));
    e.target.value = "";
  };

  const onCropped = async (blob) => {
    setUploading(true);
    try {
      const url = await uploadImage(blob, "listing.jpg");
      setForm((f) => {
        const images = [...(f.images || []), url];
        return { ...f, images, image_url: f.image_url || images[0] };
      });
      toast.success("Photo added");
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((f) => {
    const images = (f.images || []).filter((_, i) => i !== idx);
    return { ...f, images, image_url: images[0] || "" };
  });

  const makeCover = (idx) => setForm((f) => {
    const images = [...(f.images || [])];
    const [pick] = images.splice(idx, 1);
    images.unshift(pick);
    return { ...f, images, image_url: images[0] || "" };
  });

  const reorder = (from, to) => setForm((f) => {
    if (from === null || from === to) return f;
    const images = [...(f.images || [])];
    const [moved] = images.splice(from, 1);
    images.splice(to, 0, moved);
    return { ...f, images, image_url: images[0] || "" };
  });

  const submit = async () => {
    if (!form.title || !form.location) {
      toast.error("Title and location are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, image_url: (form.images && form.images[0]) || form.image_url || "" };
      if (initial?.id) {
        await updateListing(initial.id, payload);
        toast.success("Listing updated");
      } else {
        await createListing(payload);
        toast.success("Listing added");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="listing-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {initial?.id ? "Edit Listing" : "Add Listing"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="3 BHK Builder Floor, Sector 14" className="mt-1.5" data-testid="form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deal Type</Label>
              <Select value={form.deal_type} onValueChange={(v) => set("deal_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="form-deal-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEALS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Location</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)}
              placeholder="Sector 33, Hisar" className="mt-1.5" data-testid="form-location" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Price</Label>
              <Input value={form.price} onChange={(e) => set("price", e.target.value)}
                placeholder="₹85 Lakh" className="mt-1.5" data-testid="form-price" />
            </div>
            <div>
              <Label>Area</Label>
              <Input value={form.area} onChange={(e) => set("area", e.target.value)}
                placeholder="200 sq. yd." className="mt-1.5" data-testid="form-area" />
            </div>
            <div>
              <Label>Beds</Label>
              <Input value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}
                placeholder="3 BHK" className="mt-1.5" data-testid="form-bedrooms" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="Short description..." className="mt-1.5" rows={3} data-testid="form-description" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Photos {form.images?.length ? `(${form.images.length})` : ""}</Label>
              <label className="inline-flex items-center gap-2 rounded-full border border-[#131311] px-4 py-1.5 text-xs font-semibold cursor-pointer hover:bg-[#131311] hover:text-white transition-colors" data-testid="form-add-photo">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Add photo
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="form-image-upload" />
              </label>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="form-gallery">
              {(form.images || []).map((img, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { reorder(dragIdx, idx); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`relative group rounded-xl overflow-hidden border aspect-[16/10] bg-[#f4efdf] cursor-move transition-opacity ${dragIdx === idx ? "opacity-40 border-[#c5a059]" : "border-[#e7e3d7]"}`}
                  data-testid={`form-gallery-item-${idx}`}
                >
                  <img src={resolveImage(img)} alt={`photo ${idx + 1}`} className="h-full w-full object-cover pointer-events-none" />
                  {idx === 0 && (
                    <span className="absolute top-1 left-1 rounded-full bg-[#131311] text-[#f0d98a] px-2 py-0.5 text-[9px] font-semibold">Cover</span>
                  )}
                  <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                    {idx !== 0 && (
                      <button type="button" onClick={() => makeCover(idx)} className="rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold text-[#131311]" data-testid={`form-set-cover-${idx}`}>
                        Set cover
                      </button>
                    )}
                    <button type="button" onClick={() => removeImage(idx)} className="rounded-full bg-red-600 text-white h-6 w-6 grid place-items-center" data-testid={`form-remove-photo-${idx}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
              {(!form.images || form.images.length === 0) && (
                <div className="col-span-3 rounded-xl border border-dashed border-[#d8d2c2] bg-[#f4efdf]/50 py-6 text-center text-xs text-[#8a8578]">
                  No photos yet — click "Add photo" to upload (you can crop & zoom each one).
                </div>
              )}
            </div>
            {form.images && form.images.length > 1 && (
              <p className="mt-2 text-[11px] text-[#8a8578]">Drag photos to reorder · the first photo is the cover.</p>
            )}
            <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)}
              placeholder="or paste a cover image URL" className="text-xs mt-2" data-testid="form-image-url" />
          </div>
          <div className="flex items-center justify-between rounded-2xl border border-[#e7e3d7] bg-[#f4efdf]/60 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-[#131311]">Show on homepage carousel</div>
              <div className="text-xs text-[#8a8578]">Featured listings appear in the moving carousel. All listings appear on the Properties page.</div>
            </div>
            <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="form-featured" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#131311] hover:bg-[#9a7b38] rounded-full" data-testid="form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Listing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ImageCropDialog
      open={!!cropSrc}
      src={cropSrc}
      aspect={1.6}
      title="Adjust listing photo"
      busy={uploading}
      onApply={onCropped}
      onCancel={() => { if (cropSrc) URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
    />
    </>
  );
}

function ListingsPanel() {
  const [listings, setListings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(() => {
    getListings().then(setListings).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    try {
      await deleteListing(l.id);
      toast.success("Listing deleted");
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#8a8578]">{listings.length} {listings.length === 1 ? "listing" : "listings"}</p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#131311] hover:bg-[#9a7b38] rounded-full" data-testid="admin-add-listing">
          <Plus className="h-4 w-4 mr-2" /> Add Listing
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {listings.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white border border-[#e7e3d7] overflow-hidden" data-testid={`admin-listing-${l.id}`}>
            <div className="relative h-40 bg-[#f4efdf]">
              {l.image_url && <img src={resolveImage(l.image_url)} alt={l.title} className="h-full w-full object-cover" />}
              {l.featured && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[#131311] text-[#f0d98a] px-2.5 py-1 text-[10px] font-semibold" data-testid={`admin-featured-badge-${l.id}`}>
                  <Star className="h-3 w-3 fill-[#f0d98a]" /> Featured
                </span>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-[#9a7b38] font-semibold">
                <span>{l.category}</span><span>·</span><span>{l.deal_type}</span>
              </div>
              <h3 className="font-serif text-lg font-semibold text-[#131311] mt-1 line-clamp-1">{l.title}</h3>
              <p className="text-sm text-[#8a8578]">{l.location}</p>
              {l.price && <p className="text-sm font-semibold text-[#131311] mt-1">{l.price}</p>}
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => { setEditing(l); setDialogOpen(true); }} data-testid={`admin-edit-${l.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200" onClick={() => remove(l)} data-testid={`admin-delete-${l.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ListingForm open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={refresh} />
    </div>
  );
}

function TeamPanel() {
  const [members, setMembers] = useState(STATIC_TEAM);
  const [uploadingId, setUploadingId] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);

  const refresh = useCallback(() => {
    getTeam()
      .then((data) => setMembers(Array.isArray(data) && data.length ? data : STATIC_TEAM))
      .catch(() => setMembers(STATIC_TEAM));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onFile = (m, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropTarget(m);
    setCropSrc(URL.createObjectURL(file));
    e.target.value = "";
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropTarget(null);
  };

  const onCropped = async (blob) => {
    const m = cropTarget;
    if (!m) return;
    setUploadingId(m.id);
    try {
      const url = await uploadImage(blob, "team.jpg");
      await updateTeamMember(m.id, { name: m.name, role: m.role, detail: m.detail, bio: m.bio, photo: url });
      toast.success(`${m.name}'s photo updated`);
      refresh();
      closeCrop();
    } catch { toast.error("Upload failed"); }
    finally { setUploadingId(null); }
  };

  const removePhoto = async (m) => {
    try {
      await updateTeamMember(m.id, { name: m.name, role: m.role, detail: m.detail, bio: m.bio, photo: "" });
      toast.success("Photo removed");
      refresh();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <p className="text-sm text-[#8a8578] mb-6">Upload a real photo for each team member. Members without a photo show an "Image not available" placeholder on the site.</p>
      <div className="grid sm:grid-cols-3 gap-5">
        {members.map((m) => (
          <div key={m.id} className="rounded-2xl bg-white border border-[#e7e3d7] overflow-hidden" data-testid={`admin-team-${m.id}`}>
            <div className="relative aspect-[4/5] bg-[#0b0b0a] grid place-items-center">
              {m.photo ? (
                <img src={resolveImage(m.photo)} alt={m.name} className="absolute inset-0 h-full w-full object-cover" data-testid={`admin-team-photo-${m.id}`} />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#6b6558]">
                  <ImageOff className="h-7 w-7" strokeWidth={1.4} />
                  <span className="text-[10px] tracking-[0.22em] uppercase">Image not available</span>
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-serif text-lg font-semibold text-[#131311]">{m.name}</h3>
              <p className="text-xs text-[#9a7b38] font-medium">{m.role}</p>
              <div className="mt-3 flex gap-2">
                <label className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#131311] px-3 py-2 text-sm font-semibold cursor-pointer hover:bg-[#131311] hover:text-white transition-colors" data-testid={`admin-team-upload-${m.id}`}>
                  {uploadingId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {m.photo ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(m, e)} />
                </label>
                {m.photo && (
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => removePhoto(m)} data-testid={`admin-team-remove-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <ImageCropDialog
        open={!!cropSrc}
        src={cropSrc}
        aspect={4 / 5}
        title={cropTarget ? `Adjust ${cropTarget.name}'s photo` : "Adjust photo"}
        busy={!!uploadingId}
        onApply={onCropped}
        onCancel={closeCrop}
      />
    </div>
  );
}

function EnquiriesPanel() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    getEnquiries().then(setEnquiries).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    try { await deleteEnquiry(id); refresh(); } catch { toast.error("Failed"); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#9a7b38]" /></div>;

  return (
    <div>
      <p className="text-sm text-[#8a8578] mb-6">{enquiries.length} property {enquiries.length === 1 ? "enquiry" : "enquiries"} logged from WhatsApp clicks.</p>
      {enquiries.length === 0 ? (
        <div className="py-12 text-center text-[#8a8578]" data-testid="enquiries-empty">No enquiries yet. They'll appear here when visitors tap "Enquire" on a property.</div>
      ) : (
        <div className="space-y-3" data-testid="enquiries-list">
          {enquiries.map((e) => (
            <div key={e.id} className="rounded-2xl bg-white border border-[#e7e3d7] p-4 flex items-center justify-between gap-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-10 w-10 shrink-0 rounded-full bg-[#f4efdf] grid place-items-center text-[#9a7b38]">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#131311] truncate">{e.title}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#8a8578] mt-0.5">
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>}
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(e.created_at)}</span>
                    <span className="rounded-full bg-[#f4efdf] px-2 py-0.5 uppercase tracking-wide">{e.source}</span>
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 shrink-0" onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    getMe().then(setUser).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-[#faf9f5]"><Loader2 className="h-6 w-6 animate-spin text-[#9a7b38]" /></div>;
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <LoginView onSuccess={setUser} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#e7e3d7] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="font-serif text-lg font-semibold text-[#131311]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="inline-flex items-center gap-2 rounded-full border border-[#e0dccf] px-4 py-2 text-sm font-medium text-[#52514c] hover:text-[#131311]" data-testid="admin-view-site">
              <Home className="h-4 w-4" /> View Site
            </a>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-serif text-3xl font-semibold text-[#131311] mb-6">Manage your website</h1>
        <Tabs defaultValue="listings">
          <TabsList className="mb-8">
            <TabsTrigger value="listings" data-testid="tab-listings"><Building2 className="h-4 w-4 mr-2" /> Listings</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team"><Users className="h-4 w-4 mr-2" /> Team Photos</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><MessageSquare className="h-4 w-4 mr-2" /> Enquiries</TabsTrigger>
          </TabsList>
          <TabsContent value="listings"><ListingsPanel /></TabsContent>
          <TabsContent value="team"><TeamPanel /></TabsContent>
          <TabsContent value="enquiries"><EnquiriesPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
